import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Icon,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiMail, FiLock, FiUserPlus, FiLogIn, FiShield, FiCheck } from 'react-icons/fi';
import { LINK_COLOR, LOGIN_PAGE_ROUTE } from '../constants';
import { register } from '../services/user-service';
import { HttpStatusCode } from '../models/http-status-code';
import ColorModeToggle from '../components/color-mode-toggle';

function Registration(): JSX.Element {
  const [result, setResult] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();



  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('green.700', 'green.300');
  const successCardBorder = useColorModeValue('green.100', 'gray.700');
  const successCardHeaderBg = useColorModeValue('green.500', 'green.600');
  const greenCardBorder = useColorModeValue('green.100', 'gray.700');
  const greenCardHeaderBg = useColorModeValue('green.500', 'green.600');
  const bgGradientOverlay = useColorModeValue(
    'linear(135deg, rgba(247, 250, 252, 0.85) 0%, rgba(240, 253, 244, 0.85) 100%)',
    'linear(135deg, rgba(26, 32, 44, 0.85) 0%, rgba(45, 55, 72, 0.85) 100%)'
  );

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      passwordConfirmation: ''
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Mmm... Esto no parece una dirección de correo electrónico válida.')
        .required('Necesitamos saber tu dirección de correo electrónico.'),
      password: Yup.string()
        .required('Tenés que definir una contraseña segura para tu cuenta.')
        .min(5, 'La contraseña debe tener al menos 5 caracteres.')
        .max(255, 'La contraseña no puede tener más de 255 caracteres.'),
      passwordConfirmation: Yup.string()
        .required('Tenés que confirmar tu contraseña.')
        .min(5, 'La contraseña debe tener al menos 5 caracteres.')
        .max(255, 'La contraseña no puede tener más de 255 caracteres.')
        .test(
          'password-confirmation',
          'Las contraseñas no coinciden.',
          (value, context) => value === context.parent.password
        )
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const response = await register(values.email, values.password);

      switch (response.statusCode) {
        case HttpStatusCode.Created: {
          setIsSuccess(true);
          toast({
            id: 'register-response-toast',
            title: '¡Todo de diez! 👌🏻',
            description: 'El registro fue exitoso ✅ Te enviamos un correo electrónico a tu casilla para validar tu cuenta.',
            status: 'success',
                      duration: 5000,
          isClosable: true,
          position: 'bottom-right'
        });
        // Navigate after 2 seconds to let user see success message
        setTimeout(() => navigate(LOGIN_PAGE_ROUTE), 2000);
          break;
        }
        case HttpStatusCode.BadRequest: {
          setResult(response.message === 'Username already in use'
            ? 'El correo electrónico ingresado ya está en uso.'
            : '¡Uh! Algo salió mal. Intentalo de nuevo más tarde...');
          break;
        }
        default: {
          setResult('Algo no anda bien pero seguramente lo vamos a solucionar pronto. Intentalo de nuevo más tarde...');
          break;
        }
      }

      setIsLoading(false);
    }
  });

  const handleShowPasswordClick = (): void => setShowPassword(!showPassword);
  const handleShowPasswordConfirmationClick = (): void => setShowPasswordConfirmation(!showPasswordConfirmation);

  if (isSuccess) {
    return (
      <Box
        minH="100vh"
        position="relative"
        py={8}
        className="fade-in-up"
        backgroundImage="url('/pacifico-sede.gif')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgGradient: bgGradientOverlay,
          zIndex: 1
        }}
      >
        <Container maxW="lg" centerContent position="relative" zIndex={2}>
          <Flex
            direction="column"
            align="center"
            w="full"
            justify="center"
            minH="60vh"
          >
            <Card
              w="full"
              maxW="md"
              shadow="2xl"
              bg={cardBg}
              borderRadius="2xl"
              border="1px"
              borderColor={successCardBorder}
              className="card-hover"
              overflow="hidden"
            >
              <CardHeader
                bg={successCardHeaderBg}
                color="white"
                textAlign="center"
                py={6}
              >
                <VStack spacing={2}>
                  <Icon as={FiCheck} boxSize={10} />
                  <Heading size="lg" fontWeight="bold">
                    ¡Registro exitoso!
                  </Heading>
                  <Text fontSize="sm" opacity={0.9}>
                    Te enviamos un correo para validar tu cuenta
                  </Text>
                </VStack>
              </CardHeader>
              <CardBody p={8} textAlign="center">
                <Text color={textColor} mb={4}>
                  En breve serás redirigido a la página de inicio de sesión...
                </Text>
                <Button
                  as="a"
                  href={LOGIN_PAGE_ROUTE}
                  colorScheme="green"
                  leftIcon={<FiLogIn />}
                >
                  Ir a iniciar sesión
                </Button>
              </CardBody>
            </Card>
          </Flex>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      position="relative"
      py={8}
      className="fade-in-up"
      backgroundImage="url('/pacifico-sede.gif')"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgGradient: bgGradientOverlay,
        zIndex: 1
      }}
    >
      <Container maxW="lg" centerContent position="relative" zIndex={2}>
        <Flex
          direction="column"
          align="center"
          w="full"
          mb={8}
        >
          <HStack justify="space-between" w="full" mb={4}>
            <Box />
            <ColorModeToggle />
          </HStack>
          
          <Card
            w="full"
            maxW="md"
            shadow="2xl"
            bg={cardBg}
            borderRadius="2xl"
            border="1px"
            borderColor={greenCardBorder}
            className="card-hover"
            overflow="hidden"
          >
            <CardHeader
              bg={greenCardHeaderBg}
              color="white"
              textAlign="center"
              py={6}
            >
              <VStack spacing={2}>
                <Icon as={FiUserPlus} boxSize={8} />
                <Heading size="lg" fontWeight="bold">
                  Crear cuenta
                </Heading>
                <Text fontSize="sm" opacity={0.9}>
                  Registrate para acceder a la sede virtual
                </Text>
              </VStack>
            </CardHeader>

            <CardBody p={8}>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={6}>
                  <FormControl
                    isInvalid={
                      (formik.touched.email ?? false) && formik.errors.email != null
                    }
                  >
                    <FormLabel
                      htmlFor="email"
                      color={textColor}
                      fontWeight="semibold"
                    >
                      Correo electrónico
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formik.values.email}
                        placeholder="juan.perez@gmail.com"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        size="lg"
                        borderRadius="lg"
                        focusBorderColor="green.500"
                        _hover={{
                          borderColor: 'green.300'
                        }}
                      />
                      <InputRightElement pointerEvents="none" height="3rem">
                        <Icon as={FiMail} color="gray.400" />
                      </InputRightElement>
                    </InputGroup>
                    <FormHelperText color={textColor} fontSize="sm" mt={2}>
                      <Icon as={FiShield} mr={1} />
                      Nunca compartimos tu email con terceros
                    </FormHelperText>
                    <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={
                      (formik.touched.password ?? false) &&
                      formik.errors.password != null
                    }
                  >
                    <FormLabel
                      htmlFor="password"
                      color={textColor}
                      fontWeight="semibold"
                    >
                      Contraseña
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="••••••••"
                        size="lg"
                        borderRadius="lg"
                        focusBorderColor="green.500"
                        _hover={{
                          borderColor: 'green.300'
                        }}
                      />
                                             <InputRightElement height="3rem">
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={handleShowPasswordClick}
                           aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                           p={2}
                         >
                           <Icon as={showPassword ? FiEyeOff : FiEye} />
                         </Button>
                       </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={
                      (formik.touched.passwordConfirmation ?? false) &&
                      formik.errors.passwordConfirmation != null
                    }
                  >
                    <FormLabel
                      htmlFor="password-confirmation"
                      color={textColor}
                      fontWeight="semibold"
                    >
                      Confirmar contraseña
                    </FormLabel>
                    <InputGroup>
                      <Input
                        id="password-confirmation"
                        name="passwordConfirmation"
                        type={showPasswordConfirmation ? 'text' : 'password'}
                        value={formik.values.passwordConfirmation}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="••••••••"
                        size="lg"
                        borderRadius="lg"
                        focusBorderColor="green.500"
                        _hover={{
                          borderColor: 'green.300'
                        }}
                      />
                                             <InputRightElement height="3rem">
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={handleShowPasswordConfirmationClick}
                           aria-label={showPasswordConfirmation ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                           p={2}
                         >
                           <Icon as={showPasswordConfirmation ? FiEyeOff : FiEye} />
                         </Button>
                       </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>
                      {formik.errors.passwordConfirmation}
                    </FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="green"
                    size="lg"
                    borderRadius="lg"
                    isLoading={isLoading}
                    loadingText="Registrando..."
                    leftIcon={<FiUserPlus />}
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                  >
                    Registrarse
                  </Button>

                  {result && (
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      <AlertDescription color="red.700">
                        {result}
                      </AlertDescription>
                    </Alert>
                  )}
                </Stack>
              </form>
            </CardBody>
          </Card>

          <VStack spacing={4} mt={8} textAlign="center">
            <Divider />
            
            <VStack spacing={2}>
              <Text color={headingColor} fontWeight="semibold" fontSize="lg">
                ¿Ya tenés una cuenta?
              </Text>
              <Text color={textColor}>
                Podés iniciar sesión haciendo click aquí
              </Text>
              <Button
                as="a"
                href={LOGIN_PAGE_ROUTE}
                variant="outline"
                colorScheme="green"
                size="md"
                leftIcon={<FiLogIn />}
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md'
                }}
              >
                Iniciar sesión
              </Button>
            </VStack>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
}

export default Registration;
